import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { ofimaPedidosService } from '../api/ofimaPedidosService'
import type { OfimaPedidoDetalle, OfimaPedidoListItem, OfimaPedidosPagination } from '../types/ofimaPedidos'
import { extractAxiosApiErrorMessage } from '../utils/apiErrorMessage'
import { validateOfimaOrdenQuery } from '../utils/ofimaPedidos'

const DEFAULT_LIMIT = 20

export function useOfimaPedidos(tipodcto?: string) {
  const tipo = tipodcto?.trim() ?? ''
  const [orden, setOrden] = useState('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(DEFAULT_LIMIT)
  const [queryVersion, setQueryVersion] = useState(0)
  const [pedidos, setPedidos] = useState<OfimaPedidoListItem[]>([])
  const [pagination, setPagination] = useState<OfimaPedidosPagination | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const tipodctoRef = useRef(tipo)

  const searchOrden = useCallback((value: string) => {
    const next = value.trim()
    const ordenError = validateOfimaOrdenQuery(next)
    if (ordenError) {
      toast.error(ordenError)
      return
    }
    setOrden(next)
    setPage(1)
    setQueryVersion((current) => current + 1)
  }, [])

  const changeLimit = useCallback((nextLimit: number) => {
    setLimit(nextLimit)
    setPage(1)
  }, [])

  useEffect(() => {
    let cancelled = false

    const tiposChanged = tipodctoRef.current !== tipo
    if (tiposChanged) {
      tipodctoRef.current = tipo
      if (page !== 1) {
        setPage(1)
        return () => {
          cancelled = true
        }
      }
    }

    const load = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await ofimaPedidosService.getPedidos({
          orden,
          page,
          limit,
          tipodcto: tipo || undefined
        })
        if (cancelled) return
        setPedidos(response.data)
        setPagination(response.pagination ?? null)
      } catch (err) {
        if (cancelled) return
        const message = extractAxiosApiErrorMessage(err, 'No fue posible consultar los pedidos')
        setError(message)
        setPedidos([])
        setPagination(null)
        toast.error(message)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [limit, orden, page, queryVersion, tipo])

  const totalPages = Math.max(1, pagination?.totalPages ?? 1)

  return {
    orden,
    page,
    setPage,
    limit,
    changeLimit,
    pedidos,
    pagination,
    totalPages,
    isLoading,
    error,
    searchOrden
  }
}

export function useOfimaPedidoDetalle(orden: string | null, enabled: boolean, tipodcto?: string) {
  const [detalle, setDetalle] = useState<OfimaPedidoDetalle | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled || !orden) {
      setDetalle(null)
      setError(null)
      setIsLoading(false)
      return
    }

    let cancelled = false

    const load = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const data = await ofimaPedidosService.getPedidoByOrden(orden, tipodcto)
        if (!cancelled) setDetalle(data)
      } catch (err) {
        if (cancelled) return
        const message = extractAxiosApiErrorMessage(err, 'No fue posible consultar el pedido')
        setDetalle(null)
        setError(message)
        toast.error(message)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [enabled, orden, tipodcto])

  return { detalle, isLoading, error }
}
